/* =============================================================================
   Sticky Add-to-Cart Bar — PDP  (.ae theme build — i18n via Liquid, RTL-aware)
   assets/sticky-atc-bar.js — rendered by snippets/sticky-atc-bar.liquid
   10 Aug 2026

   WHAT CHANGED vs the supplied build
   ----------------------------------
   1. UMF_PRICE_MAP (hardcoded prices) — DELETED.
      All prices now come from Shopify's own product JSON (/products/<handle>.js),
      so they are always the real, current prices for every product.
   2. Subscription prices — now read from each variant's selling_plan_allocations
      for the selling plan the shopper actually has selected, instead of assuming
      a flat 20% off. (The UMF products carry BOTH 10% legacy plans and 20% plans.)
   3. "Subscribe & Save 20%" label — the % is now derived from live data, because
      it is not always 20%.
   4. One-time / subscription prices no longer read from `.onetime-radio
      .price-label` / `.subscription-radio .price-label`. Those selectors match
      NOTHING on the live PDP (Recharge v2 renders in a shadow root), so they
      always returned empty strings.
   5. Fail-safe: if the product JSON cannot be loaded, the bar does NOT render.
      Showing no bar is strictly better than showing a wrong price.

   Everything else (dropdown UX, ghost-click blocker, touch handling, sold-out
   state, Recharge shadow-DOM sync, Gorgias offset) is unchanged.
============================================================================= */

(function () {
  'use strict';

  const CONFIG = {
    stickyBarId: 'sticky-cta-bar',
    rechargeWidgetId: 'RechargeWidget_1456132456506',
    // The bar appears as soon as the "ACCREDITATIONS & QUALITY ASSURANCE"
    // section STARTS — i.e. its top edge enters the viewport from the bottom —
    // on desktop and mobile alike. showOffset is a FALLBACK, used only if no
    // anchor can be resolved.
    showOffset: 300,
    // px the section top must be ABOVE the fold before the bar appears.
    // 0 = the instant the section begins to appear at the bottom of the screen.
    sectionEnterGap: 0,
    mobileBreakpoint: 768,
    productTitle: '',
    productImage: ''
  };

  // ---------------------------------------------------------------------------
  // PRODUCT DATA — the single source of truth for every price on the bar
  // ---------------------------------------------------------------------------
  const productData = {
    loaded: false,
    handle: '',
    optionCount: 1,
    variants: []   // [{ id, title, options[], price, available, image, allocations[] }]
  };

  const state = {
    currentVariantId: null,
    currentVariantLabel: '',
    currentVariantImage: '',
    currentSellingPlan: null,
    isPurchaseTypeSubscription: false,
    variants: [],
    isVisible: false,
    onetimePrice: '',
    subscriptionPrice: '',
    currentPrice: '',
    currentPurchaseLabel: '',
    subscribeLabel: '',
    isSoldOut: false
  };

  function setDropdownOpenState(open) {
    document.documentElement.classList.toggle('sticky-dd-open', !!open);
  }

  let dropdownOpenTime = 0;
  let anyDropdownOpen = false;
  let syncInterval = null;
  let priceUpdateTimer = null;
  let _shadowPollInterval = null;
  let _lastShadowState = null;

  // ============================================================
  // MONEY — mirrors the theme's own money_format ("${{amount}} USD")
  // ============================================================
  function formatWithDelimiters(number, precision, thousands, decimal) {
    precision = precision == null ? 2 : precision;
    thousands = thousands || ',';
    decimal = decimal || '.';
    if (number == null || isNaN(number)) return '0';
    const fixed = (number / 100.0).toFixed(precision);
    const parts = fixed.split('.');
    const whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
    const frac = parts[1] ? decimal + parts[1] : '';
    return whole + frac;
  }

  /**
   * This theme does NOT set window.Shopify.money_format (verified live on the
   * .com PDP 10-Aug). The shop format lives on afterpay_shop_money_format
   * ("${{amount}} USD") which is what the theme actually renders. Resolve in
   * order and cache, so the bar reads "$18.88 USD" like the rest of the page.
   */
  let _moneyFormat = null;
  let _inlineMoneyFormat = null;
  // UI strings come from the Liquid snippet (theme locale files), so this asset
  // is store-agnostic. English defaults are the .com wording and act as a
  // fallback if a key is ever missing.
  const T = {
    addToCart: 'Add to cart for',
    soldOut: 'Sold Out',
    selectVariant: 'Select Variant',
    oneTime: 'One-time purchase',
    subscribe: 'Subscribe & Save',
    noVariants: 'No variants available'
  };   // shop.money_format, rendered by the Liquid snippet
  function resolveMoneyFormat() {
    if (_moneyFormat) return _moneyFormat;
    const candidates = [
      _inlineMoneyFormat,                       // shop.money_format from Liquid
      window.Shopify && window.Shopify.money_format,
      window.afterpay_shop_money_format
    ];
    for (let i = 0; i < candidates.length; i++) {
      if (typeof candidates[i] === 'string' && /\{\{/.test(candidates[i])) {
        _moneyFormat = candidates[i];
        return _moneyFormat;
      }
    }
    _moneyFormat = '${{amount}}';
    return _moneyFormat;
  }

  function formatMoney(cents) {
    if (cents == null || isNaN(cents)) return '';
    // Respect Shopify Markets currency conversion if it is active.
    const rate = parseFloat(
      (window.Shopify && window.Shopify.currency && window.Shopify.currency.rate) || 1
    ) || 1;
    const value = Math.round(Number(cents) * rate);

    const fmt = resolveMoneyFormat();
    const placeholder = /\{\{\s*(\w+)\s*\}\}/;
    const match = fmt.match(placeholder);
    if (!match) return fmt;

    switch (match[1]) {
      case 'amount_no_decimals':
        return fmt.replace(placeholder, formatWithDelimiters(value, 0));
      case 'amount_with_comma_separator':
        return fmt.replace(placeholder, formatWithDelimiters(value, 2, '.', ','));
      case 'amount_no_decimals_with_comma_separator':
        return fmt.replace(placeholder, formatWithDelimiters(value, 0, '.', ','));
      case 'amount_with_space_separator':
        return fmt.replace(placeholder, formatWithDelimiters(value, 2, ' ', ','));
      case 'amount_no_decimals_with_space_separator':
        return fmt.replace(placeholder, formatWithDelimiters(value, 0, ' ', ','));
      case 'amount_with_apostrophe_separator':
        return fmt.replace(placeholder, formatWithDelimiters(value, 2, "'", '.'));
      default:
        return fmt.replace(placeholder, formatWithDelimiters(value, 2));
    }
  }

  function escapeAttr(str) {
    return String(str == null ? '' : str)
      .replace(/&/g, '&amp;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }

  // ============================================================
  // LOAD PRODUCT JSON
  // ============================================================
  function getProductHandle() {
    try {
      if (window.ShopifyAnalytics &&
          window.ShopifyAnalytics.meta &&
          window.ShopifyAnalytics.meta.product &&
          window.ShopifyAnalytics.meta.product.handle) {
        return window.ShopifyAnalytics.meta.product.handle;
      }
    } catch (e) {}
    const m = window.location.pathname.match(/\/products\/([^/?#]+)/);
    return m ? m[1] : '';
  }

  /**
   * Theme build: snippets/sticky-atc-bar.liquid renders the product data
   * inline, so there is no network round-trip and no race with first paint.
   * The fetch below is kept only as a fallback for any template that does not
   * render the snippet.
   */
  function readInlineData() {
    const tag = document.getElementById('StickyAtcData');
    if (!tag) return false;
    let d;
    try {
      d = JSON.parse(tag.textContent);
    } catch (e) {
      console.warn('[sticky-atc] inline product data is unparseable:', e);
      return false;
    }
    if (!d || !d.variants || !d.variants.length) return false;

    if (d.moneyFormat) _inlineMoneyFormat = d.moneyFormat;
    if (d.i18n) Object.keys(d.i18n).forEach(function (k) { if (d.i18n[k]) T[k] = d.i18n[k]; });
    productData.handle = d.handle || '';
    productData.optionCount = d.optionCount || 1;
    productData.variants = d.variants.map(function (v) {
      return {
        id: String(v.id),
        title: v.title,
        options: v.options || [],
        price: v.price,
        available: !!v.available,
        image: v.image || '',
        allocations: (v.allocations || []).map(function (a) {
          return { selling_plan_id: a.selling_plan_id, price: a.price };
        })
      };
    });
    productData.loaded = productData.variants.length > 0;
    return productData.loaded;
  }

  function loadProductData() {
    if (readInlineData()) return Promise.resolve(true);

    const handle = getProductHandle();
    if (!handle) return Promise.resolve(false);

    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    const url = root.replace(/\/$/, '') + '/products/' + handle + '.js';

    return fetch(url, { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
      .then(function (r) {
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.json();
      })
      .then(function (p) {
        productData.handle = p.handle;
        productData.optionCount = (p.options && p.options.length) || 1;
        productData.variants = (p.variants || []).map(function (v) {
          return {
            id: String(v.id),
            title: v.title,
            options: v.options || [v.option1, v.option2, v.option3].filter(Boolean),
            price: v.price,
            available: !!v.available,
            image: (v.featured_image && v.featured_image.src) || '',
            allocations: v.selling_plan_allocations || []
          };
        });
        productData.loaded = productData.variants.length > 0;
        if (!CONFIG.productImage && p.featured_image) CONFIG.productImage = p.featured_image;
        if (!CONFIG.productTitle && p.title) CONFIG.productTitle = p.title;
        return productData.loaded;
      })
      .catch(function (err) {
        console.warn('[sticky-cta] could not load product data — bar suppressed:', err);
        return false;
      });
  }

  // ============================================================
  // PRICE ENGINE — everything derived, nothing hardcoded
  // ============================================================
  function getSellingPlanInput() {
    return document.querySelector('input[name="selling_plan"]');
  }

  function getActiveSellingPlanId() {
    const input = getSellingPlanInput();
    const v = input && input.value;
    return v && v !== '0' && v !== '' ? String(v) : null;
  }

  function isSubscriptionActiveFromDOM() {
    return !!getActiveSellingPlanId();
  }

  /**
   * Subscription price for a variant, in cents.
   * Prefers the plan the shopper actually has selected. Before any plan is
   * selected, falls back to the best (lowest) allocation available — the offer
   * the shopper can actually obtain. Returns null when the variant has no
   * subscription option at all.
   */
  function subscriptionPriceFor(variant) {
    if (!variant || !variant.allocations || !variant.allocations.length) return null;
    const activeId = getActiveSellingPlanId();
    if (activeId) {
      for (let i = 0; i < variant.allocations.length; i++) {
        if (String(variant.allocations[i].selling_plan_id) === activeId) {
          return variant.allocations[i].price;
        }
      }
    }
    let best = variant.allocations[0].price;
    for (let i = 1; i < variant.allocations.length; i++) {
      if (variant.allocations[i].price < best) best = variant.allocations[i].price;
    }
    return best;
  }

  function currentVariant() {
    if (!state.currentVariantId) return null;
    for (let i = 0; i < productData.variants.length; i++) {
      if (productData.variants[i].id === String(state.currentVariantId)) return productData.variants[i];
    }
    return null;
  }

  /** Price shown for a variant, honouring the current purchase type. */
  function displayPriceFor(variant) {
    if (!variant) return '';
    if (state.isPurchaseTypeSubscription) {
      const sub = subscriptionPriceFor(variant);
      if (sub != null) return formatMoney(sub);
    }
    return formatMoney(variant.price);
  }

  /** "Subscribe & Save 12%" — % derived from live data, never assumed. */
  function computeSubscribeLabel() {
    const v = currentVariant();
    if (!v || !v.price) return T.subscribe;
    const sub = subscriptionPriceFor(v);
    if (sub == null) return T.subscribe;
    const pct = Math.round((1 - sub / v.price) * 100);
    // composed here rather than via a Liquid placeholder — a literal {pct}
    // inside a Liquid output tag is a syntax error (hit on .ae 10-Aug).
    return pct > 0 ? T.subscribe + ' ' + pct + '%' : T.subscribe;
  }

  /** Recomputes every price on the bar from product data. */
  function recomputePrices() {
    const v = currentVariant();

    state.variants = productData.variants.map(function (pv) {
      return {
        id: pv.id,
        label: pv.title,
        image: pv.image || CONFIG.productImage,
        available: pv.available,
        price: displayPriceFor(pv)
      };
    });

    if (v) {
      state.onetimePrice = formatMoney(v.price);
      const sub = subscriptionPriceFor(v);
      state.subscriptionPrice = sub == null ? '' : formatMoney(sub);
      state.currentPrice = displayPriceFor(v);
      state.currentVariantLabel = v.title;
      state.currentVariantImage = v.image || CONFIG.productImage;
    } else {
      state.onetimePrice = '';
      state.subscriptionPrice = '';
      state.currentPrice = '';
    }

    state.subscribeLabel = computeSubscribeLabel();
    state.currentPurchaseLabel = state.isPurchaseTypeSubscription
      ? state.subscribeLabel
      : T.oneTime;
  }

  // ============================================================
  // DOM <-> VARIANT MAPPING
  // ============================================================
  function getVariantContainer() {
    return document.querySelector('variant-selects');
  }

  function getProductForm() {
    const productForm = document.querySelector('product-form[data-section-id] form');
    if (productForm) return productForm;
    return document.querySelector('form[action*="/cart/add"]');
  }

  /** Reads the currently-checked option value from each option group, in order. */
  function getSelectedOptionValues() {
    const container = getVariantContainer();
    if (!container) return [];
    const values = [];
    const groups = container.querySelectorAll('fieldset, .product-form__input');

    groups.forEach(function (group) {
      const checked = group.querySelector('input[type="radio"]:checked');
      if (checked) { values.push(checked.value); return; }
      const sel = group.querySelector('select');
      if (sel && sel.selectedIndex >= 0) values.push(sel.options[sel.selectedIndex].value);
    });

    if (!values.length) {
      const c = container.querySelector('input[type="radio"]:checked');
      if (c) {
        values.push(c.value);
      } else {
        const s = container.querySelector('select');
        if (s && s.selectedIndex >= 0) values.push(s.options[s.selectedIndex].value);
      }
    }
    return values;
  }

  function findVariantByOptions(values) {
    if (!values.length) return null;
    for (let i = 0; i < productData.variants.length; i++) {
      const v = productData.variants[i];
      let ok = true;
      for (let j = 0; j < values.length; j++) {
        if (String(v.options[j]) !== String(values[j])) { ok = false; break; }
      }
      if (ok) return v;
    }
    return null;
  }

  /** Authoritative: the hidden form input carries the real variant id. */
  function variantIdFromForm() {
    const input = document.querySelector('form[action*="/cart/add"] input[name="id"]');
    return input && input.value ? String(input.value) : null;
  }

  function resolveCurrentVariant() {
    const fromForm = variantIdFromForm();
    if (fromForm) {
      for (let i = 0; i < productData.variants.length; i++) {
        if (productData.variants[i].id === fromForm) return productData.variants[i];
      }
    }
    const byOptions = findVariantByOptions(getSelectedOptionValues());
    if (byOptions) return byOptions;
    for (let i = 0; i < productData.variants.length; i++) {
      if (productData.variants[i].available) return productData.variants[i];
    }
    return productData.variants[0] || null;
  }

  // ============================================================
  // GHOST-CLICK BLOCKER
  // ============================================================
  let _blockNextClick = false;
  let _blockClickTimer = null;

  function scheduleBlockNextClick() {
    _blockNextClick = true;
    if (_blockClickTimer) clearTimeout(_blockClickTimer);
    _blockClickTimer = setTimeout(function () {
      _blockNextClick = false;
      _blockClickTimer = null;
    }, 600);
  }

  document.addEventListener('click', function (e) {
    if (_blockNextClick) {
      if (!e.target.closest('#' + CONFIG.stickyBarId)) e.preventDefault();
      _blockNextClick = false;
      if (_blockClickTimer) { clearTimeout(_blockClickTimer); _blockClickTimer = null; }
    }
  }, true);

  // ============================================================
  // SOLD-OUT DETECTION
  // ============================================================
  function detectSoldOut() {
    const v = currentVariant();
    if (v && v.available === false) return true;

    const submitBtn = document.querySelector(
      'button[type="submit"][name="add"], [id^="ProductSubmitButton-"]'
    );
    if (!submitBtn) return false;
    const isDisabled = submitBtn.disabled || submitBtn.hasAttribute('disabled');
    const text = (submitBtn.textContent || '').trim().toLowerCase();
    return isDisabled && text.includes('sold out');
  }

  function applySoldOutState() {
    const soldOut = state.isSoldOut;
    const btnStyles = soldOut
      ? 'background:#888888;color:#cccccc;border:none;padding:16px 30px;border-radius:100px;font-weight:600;font-size:12px;cursor:not-allowed;white-space:nowrap;text-transform:uppercase;touch-action:manipulation;pointer-events:none;opacity:0.7;'
      : 'background:#f1b434;color:white;border:none;padding:16px 30px;border-radius:100px;font-weight:600;font-size:12px;cursor:pointer;white-space:nowrap;text-transform:uppercase;touch-action:manipulation;';
    const btnStylesMobile = soldOut
      ? 'background:#888888;color:#cccccc;border:none;padding:16px 24px;border-radius:100px;font-weight:600;font-size:12px;cursor:not-allowed;width:100%;text-transform:uppercase;touch-action:manipulation;pointer-events:none;opacity:0.7;'
      : 'background:#f1b434;color:white;border:none;padding:16px 24px;border-radius:100px;font-weight:600;font-size:12px;cursor:pointer;width:100%;text-transform:uppercase;touch-action:manipulation;';

    const desktopBtn = document.getElementById('sticky-add-to-cart');
    const mobileBtn = document.getElementById('sticky-add-to-cart-mobile');
    if (desktopBtn) {
      desktopBtn.style.cssText = btnStyles;
      desktopBtn.disabled = soldOut;
      desktopBtn.innerHTML = soldOut
        ? T.soldOut
        : T.addToCart + ' <span id="sticky-price" style="pointer-events:none;">' + state.currentPrice + '</span>';
    }
    if (mobileBtn) {
      mobileBtn.style.cssText = btnStylesMobile;
      mobileBtn.disabled = soldOut;
      mobileBtn.innerHTML = soldOut
        ? T.soldOut
        : T.addToCart + ' <span id="sticky-price-mobile" style="pointer-events:none;">' + state.currentPrice + '</span>';
    }
  }

  function syncSoldOutState() {
    const wasSoldOut = state.isSoldOut;
    state.isSoldOut = detectSoldOut();
    if (state.isSoldOut !== wasSoldOut) applySoldOutState();
  }

  // ============================================================
  // RECHARGE V2 SHADOW DOM HELPERS
  // ============================================================
  function getRechargeShadowRoot() {
    const widget = document.querySelector('recharge-subscription-widget');
    if (!widget) return null;
    return widget.shadowRoot || null;
  }

  function triggerRechargeV2Selection(purchaseType) {
    const shadow = getRechargeShadowRoot();

    if (shadow) {
      const selectors = purchaseType === 'onetime'
        ? ['[data-rc-option="onetime"]', '[data-purchase-option="onetime"]',
           '[data-testid="onetime-option"]', '.rc-option--onetime',
           '.rc-widget__option--onetime', 'input[value="onetime"]',
           '[aria-label*="one" i]', '[aria-label*="one-time" i]']
        : ['[data-rc-option="subscription"]', '[data-purchase-option="subscription"]',
           '[data-testid="subscription-option"]', '.rc-option--subscription',
           '.rc-widget__option--subscription', 'input[value="subscription"]',
           '[aria-label*="subscri" i]'];

      for (const sel of selectors) {
        const el = shadow.querySelector(sel);
        if (el) {
          if (el.tagName === 'INPUT' && el.type === 'radio') {
            el.checked = true;
            el.dispatchEvent(new Event('change', { bubbles: true }));
            el.dispatchEvent(new Event('input', { bubbles: true }));
          } else {
            el.click();
          }
          return true;
        }
      }

      const allClickable = shadow.querySelectorAll('button, label, [role="radio"], [role="button"], input[type="radio"]');
      const keyword = purchaseType === 'onetime' ? /one.?time|one-time/i : /subscri/i;
      for (const el of allClickable) {
        const text = (el.textContent || el.getAttribute('aria-label') || el.value || '').trim();
        if (keyword.test(text)) {
          if (el.tagName === 'INPUT') {
            el.checked = true;
            el.dispatchEvent(new Event('change', { bubbles: true }));
          } else {
            el.click();
          }
          return true;
        }
      }
    }

    const sellingPlanInput = getSellingPlanInput();
    if (!sellingPlanInput) return false;

    const nativeSetter = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value').set;

    if (purchaseType === 'onetime') {
      nativeSetter.call(sellingPlanInput, '');
      sellingPlanInput.dispatchEvent(new Event('input', { bubbles: true }));
      sellingPlanInput.dispatchEvent(new Event('change', { bubbles: true }));
      return true;
    }

    if (purchaseType === 'subscription') {
      // Pick the plan from the CURRENT VARIANT's own allocations — never a
      // hardcoded plan id, because plan ids differ per product.
      const v = currentVariant();
      if (v && v.allocations.length) {
        let best = v.allocations[0];
        for (let i = 1; i < v.allocations.length; i++) {
          if (v.allocations[i].price < best.price) best = v.allocations[i];
        }
        nativeSetter.call(sellingPlanInput, String(best.selling_plan_id));
        sellingPlanInput.dispatchEvent(new Event('input', { bubbles: true }));
        sellingPlanInput.dispatchEvent(new Event('change', { bubbles: true }));
        return true;
      }
    }

    return false;
  }

  function watchSellingPlanInput() {
    const input = getSellingPlanInput();
    if (!input) return;

    if (!input._rcWatched) {
      input._rcWatched = true;
      const mo = new MutationObserver(function () {
        syncFromOriginalPurchaseType();
        schedulePriceUpdate();
      });
      mo.observe(input, { attributes: true, attributeFilter: ['value'] });
    }

    if (!input._rcValueWatched) {
      input._rcValueWatched = true;
      const descriptor = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value');
      let lastValue = input.value;
      Object.defineProperty(input, 'value', {
        get: function () { return descriptor.get.call(this); },
        set: function (val) {
          descriptor.set.call(this, val);
          if (String(val) !== String(lastValue)) {
            lastValue = String(val);
            setTimeout(function () {
              syncFromOriginalPurchaseType();
              schedulePriceUpdate();
            }, 50);
          }
        },
        configurable: true
      });
    }

    startShadowPollSync();
  }

  function startShadowPollSync() {
    if (_shadowPollInterval) return;
    _shadowPollInterval = setInterval(function () {
      const shadow = getRechargeShadowRoot();
      if (!shadow) return;

      let shadowState = null;

      const checkedRadio = shadow.querySelector('input[type="radio"]:checked');
      if (checkedRadio) shadowState = checkedRadio.value;

      if (!shadowState) {
        const activeEl = shadow.querySelector(
          '[aria-checked="true"], [data-selected="true"], .rc-option--selected, .rc-widget__option--selected, [data-rc-selected="true"]'
        );
        if (activeEl) {
          shadowState = activeEl.getAttribute('data-rc-option') ||
                        activeEl.getAttribute('data-purchase-option') ||
                        activeEl.getAttribute('data-testid') ||
                        (activeEl.textContent || '').trim().substring(0, 20);
        }
      }

      if (!shadowState) {
        const sp = getSellingPlanInput();
        shadowState = sp ? (sp.value === '' ? 'onetime' : 'subscription') : null;
      }

      if (shadowState && shadowState !== _lastShadowState) {
        _lastShadowState = shadowState;
        const isNowSubscription = shadowState !== 'onetime' && shadowState !== '' && shadowState !== '0';
        if (isNowSubscription !== state.isPurchaseTypeSubscription) {
          state.isPurchaseTypeSubscription = isNowSubscription;
          recomputePrices();
          updateStickyPurchaseDisplay();
          updateStickyVariantDisplay();
          updateStickyPrice();
          refreshStickyDropdowns();
        }
      }
    }, 500);
  }

  // ============================================================
  // INIT & SETUP
  // ============================================================
  function init() {
    addUserWayStyles();
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', bootstrap);
    } else {
      bootstrap();
    }
  }

  function bootstrap() {
    // Prices must be real before anything renders. If the product JSON is
    // unavailable we deliberately render nothing rather than guess.
    loadProductData().then(function (ok) {
      if (!ok) return;
      setup();
    });
  }

  /**
   * The .ae theme does NOT set dir="rtl" on <html> even on /ar/ pages
   * (verified 10-Aug — lang="ar" but no dir). So RTL cannot be keyed off the
   * dir attribute; detect it from the locale and stamp our own class.
   */
  function applyRtlClass() {
    const el = document.documentElement;
    const lang = (el.getAttribute('lang') || '').toLowerCase();
    const rtlLang = /^(ar|he|fa|ur)\b/.test(lang);
    const rtl = el.getAttribute('dir') === 'rtl' ||
                getComputedStyle(el).direction === 'rtl' ||
                rtlLang;
    el.classList.toggle('sticky-rtl', !!rtl);
    return !!rtl;
  }

  function addUserWayStyles() {
    applyRtlClass();
    const styleTag = document.createElement('style');
    styleTag.textContent = `
      /* NOTE: the original build set display:none on .userway_buttons_wrapper,
         which removes the accessibility widget from the page entirely. That is
         an accessibility regression, so instead the widget is LIFTED above the
         bar at runtime (see syncFloatingWidgets) and restored when it hides. */
      body .uwy.userway_p5 .userway_buttons_wrapper {
        top: auto; left: 13px; right: auto; justify-content: end;
        transition: bottom 0.25s ease;
      }
      .messenger-button-iframe-1k36w1m { margin-bottom: 70px; }
      #${CONFIG.stickyBarId} { pointer-events: auto !important; }
      #${CONFIG.stickyBarId} * { pointer-events: auto !important; }
      #${CONFIG.stickyBarId} .sticky-cta-dropdown-menu { z-index: 2147483001 !important; }

      /* ---- RTL (Arabic) ----------------------------------------------
         The bar is built with left-anchored dropdowns and margin-left:auto
         prices, which mirror wrongly on /ar/. Flip the axis rather than
         restyling: only the anchoring and the auto-margin need to change. */
      html.sticky-rtl #${CONFIG.stickyBarId} .sticky-cta-dropdown-menu {
        left: auto !important;
        right: 0 !important;
      }
      html.sticky-rtl #${CONFIG.stickyBarId} .sticky-cta-dropdown-item > span:last-child {
        margin-left: 0 !important;
        margin-right: auto !important;
      }
      html.sticky-rtl #${CONFIG.stickyBarId} .sticky-cta-dropdown-item,
      html.sticky-rtl #${CONFIG.stickyBarId} .sticky-cta-dropdown-button {
        direction: rtl;
        text-align: right;
      }
      @media (max-width: 768px) {
        /* the mobile purchase menu uses a -170px nudge to stay on screen;
           in RTL that pushes it off the opposite edge */
        html.sticky-rtl #${CONFIG.stickyBarId} #sticky-purchase-dropdown-mobile .sticky-cta-dropdown-menu {
          left: 0 !important;
          right: -170px !important;
        }
      }
      /* While a sticky dropdown is open its menu expands upward into the strip
         where the chat + accessibility launchers sit, so they land on top of the
         variant rows (reported 10-Aug, mobile). Hide them for the duration —
         transient and user-initiated, so the a11y widget is never permanently
         removed. */
      html.sticky-dd-open iframe#chat-button,
      html.sticky-dd-open iframe#chat-input,
      html.sticky-dd-open iframe[title="Gorgias live chat messenger"],
      html.sticky-dd-open body .uwy .userway_buttons_wrapper,
      html.sticky-dd-open .userway_buttons_wrapper,
      html.sticky-dd-open [class*="kl-teaser"],
      html.sticky-dd-open #rebuy-bubble-global-alert-modal {
        opacity: 0 !important;
        pointer-events: none !important;
        transition: opacity .12s ease;
      }
      @media (max-width: 768px) {
        #gorgias-chat-container, [class*="gorgias-chat"], [id*="gorgias-chat"] { pointer-events: none !important; }
        #gorgias-chat-container iframe, [class*="gorgias-chat"] iframe, [id*="gorgias-chat"] iframe { pointer-events: auto !important; }
      }
    `;
    document.head.appendChild(styleTag);
  }

  function setup() {
    extractProductInfo();

    const v = resolveCurrentVariant();
    if (v) state.currentVariantId = v.id;
    state.isPurchaseTypeSubscription = isSubscriptionActiveFromDOM();

    recomputePrices();
    state.isSoldOut = detectSoldOut();

    createStickyBar();
    attachEventListeners();
    watchSubmitButtonForSoldOut();
    watchSellingPlanInput();
    handleScroll();
    startPeriodicSync();
  }

  function watchSubmitButtonForSoldOut() {
    const productForm = document.querySelector('product-form');
    if (!productForm) return;
    const observer = new MutationObserver(function () { syncSoldOutState(); });
    observer.observe(productForm, {
      subtree: true, attributes: true,
      attributeFilter: ['disabled', 'class'],
      childList: true, characterData: true
    });
  }

  function extractProductInfo() {
    const titleSelectors = ['.product__title h1', '.product-info h1', 'h1[itemprop="name"]', '.product-single__title', '.product-title'];
    for (const selector of titleSelectors) {
      const el = document.querySelector(selector);
      if (el) { CONFIG.productTitle = el.textContent.trim().replace(/\s+/g, ' '); break; }
    }
    const imageSelectors = ['.product__media img', '.product-single__photo img', 'img[itemprop="image"]', '.product-featured-img', '#ProductPhotoImg'];
    for (const selector of imageSelectors) {
      const el = document.querySelector(selector);
      if (el && el.src) { CONFIG.productImage = el.src; break; }
    }
  }

  function atcButtonHTML(isMobile) {
    const padding = isMobile ? '16px 24px' : '16px 30px';
    const widthStyle = isMobile ? 'width:100%;' : '';
    const priceId = isMobile ? 'sticky-price-mobile' : 'sticky-price';
    const id = 'sticky-add-to-cart' + (isMobile ? '-mobile' : '');
    if (state.isSoldOut) {
      return `<button id="${id}" disabled style="background:#888888;color:#cccccc;border:none;padding:${padding};border-radius:100px;font-weight:600;font-size:12px;cursor:not-allowed;white-space:nowrap;text-transform:uppercase;touch-action:manipulation;pointer-events:none;opacity:0.7;${widthStyle}">${T.soldOut}</button>`;
    }
    return `<button id="${id}" style="background:#f1b434;color:white;border:none;padding:${padding};border-radius:100px;font-weight:600;font-size:12px;cursor:pointer;white-space:nowrap;text-transform:uppercase;touch-action:manipulation;${widthStyle}">${T.addToCart} <span id="${priceId}" style="pointer-events:none;">${state.currentPrice}</span></button>`;
  }

  // ============================================================
  // CREATE STICKY BAR
  // ============================================================
  function createStickyBar() {
    if (document.getElementById(CONFIG.stickyBarId)) return;
    const stickyBar = document.createElement('div');
    stickyBar.id = CONFIG.stickyBarId;
    stickyBar.className = 'sticky-cta-bar';
    // z-index NOTE: the original build set `z-index:1 !important` inline here
    // while the stylesheet asked for 2147483000. Inline !important wins, so the
    // bar actually rendered at z-index 1 — beneath Gorgias (2147483636) and
    // UserWay, and it trapped its own dropdowns (z 2147483001) inside a z-index:1
    // stacking context. Set it once, here, at the intended value.
    stickyBar.style.cssText = `position:fixed;bottom:0;left:0;right:0;background:rgb(49 49 49);box-shadow:0 -2px 12px rgba(0,0,0,0.08);padding:12px 20px;z-index:2147483000;transform:translateY(100%);transition:transform 0.3s;display:none;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;pointer-events:auto;`;

    const variantButtonText = state.currentVariantLabel
      ? `${state.currentVariantLabel} – ${state.currentPrice}`
      : T.selectVariant;

    stickyBar.innerHTML = `
      <div class="sticky-cta-container" style="max-width:1400px;margin:0 auto;">
        <div class="sticky-cta-desktop" style="display:flex;align-items:center;justify-content:space-between;gap:16px;">
          <div class="sticky-cta-dropdowns-group" style="display:flex;align-items:center;gap:16px;">
            <div class="sticky-cta-dropdown" id="sticky-variant-dropdown" style="position:relative;">
              <button type="button" class="sticky-cta-dropdown-button" style="display:flex;align-items:center;gap:8px;padding:16px 20px;background:#454545;border-radius:10px;font-size:12px;color:#ffffff;cursor:pointer;border:1px solid #FFFFFF1A;min-width:200px;justify-content:space-between;touch-action:manipulation;">
                <span style="display:flex;align-items:center;gap:6px;overflow:hidden;pointer-events:none;">
                  ${state.currentVariantImage ? `<img src="${escapeAttr(state.currentVariantImage)}" class="sticky-variant-img" style="width:24px;height:24px;object-fit:contain;border-radius:4px;flex-shrink:0;pointer-events:none;">` : ''}
                  <span id="sticky-variant-label" style="white-space:nowrap;overflow:hidden;text-overflow:ellipsis;pointer-events:none;">${variantButtonText}</span>
                </span>
                <svg style="width:12px;height:12px;flex-shrink:0;pointer-events:none;" viewBox="0 0 12 8" fill="none"><path d="M1 1.5L6 6.5L11 1.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
              </button>
              <div class="sticky-cta-dropdown-menu" style="position:absolute;bottom:100%;left:0;background:rgb(49,49,49);border-radius:8px;margin-bottom:8px;box-shadow:0 4px 12px rgba(0,0,0,0.1);display:none;max-height:300px;overflow-y:auto;z-index:2147483001;min-width:300px;">
                ${createVariantDropdownItems()}
              </div>
            </div>
            <div class="sticky-cta-dropdown" id="sticky-purchase-dropdown" style="position:relative;">
              <button type="button" class="sticky-cta-dropdown-button" style="display:flex;align-items:center;gap:8px;padding:20px 20px;border-radius:8px;font-size:12px;color:#ffffff;cursor:pointer;background:#454545;min-width:200px;border:1px solid #FFFFFF1A;justify-content:space-between;touch-action:manipulation;">
                <span id="sticky-purchase-label" style="pointer-events:none;">${state.currentPurchaseLabel}</span>
                <svg style="width:12px;height:12px;pointer-events:none;" viewBox="0 0 12 8" fill="none"><path d="M1 1.5L6 6.5L11 1.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
              </button>
              <div class="sticky-cta-dropdown-menu" style="position:absolute;bottom:100%;left:0;background:#454545;border:1px solid #1c1c1c;border-radius:8px;margin-bottom:8px;box-shadow:0 4px 12px rgba(0,0,0,0.1);display:none;max-height:240px;overflow-y:auto;z-index:2147483001;min-width:300px;">
                ${createPurchaseDropdownItems()}
              </div>
            </div>
          </div>
          ${atcButtonHTML(false)}
        </div>
        <div class="sticky-cta-mobile" style="display:none;">
          <div style="display:flex;gap:10px;margin-bottom:12px;">
            <div class="sticky-cta-dropdown" id="sticky-variant-dropdown-mobile" style="position:relative;flex:1;min-width:0;">
              <button type="button" class="sticky-cta-dropdown-button" style="display:flex;align-items:center;gap:8px;padding:14px 12px;background:#454545;border-radius:10px;font-size:12px;color:#ffffff;cursor:pointer;border:1px solid #FFFFFF1A;width:100%;justify-content:space-between;touch-action:manipulation;">
                <span style="display:flex;align-items:center;gap:4px;overflow:hidden;min-width:0;pointer-events:none;">
                  ${state.currentVariantImage ? `<img src="${escapeAttr(state.currentVariantImage)}" class="sticky-variant-img" style="width:20px;height:20px;object-fit:contain;border-radius:4px;flex-shrink:0;pointer-events:none;">` : ''}
                  <span id="sticky-variant-label-mobile" style="white-space:nowrap;overflow:hidden;text-overflow:ellipsis;pointer-events:none;">${variantButtonText}</span>
                </span>
                <svg style="width:12px;height:12px;flex-shrink:0;pointer-events:none;" viewBox="0 0 12 8" fill="none"><path d="M1 1.5L6 6.5L11 1.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
              </button>
              <div class="sticky-cta-dropdown-menu" style="position:absolute;bottom:100%;left:0;right:0;background:rgb(49,49,49);border-radius:8px;margin-bottom:8px;box-shadow:0 4px 12px rgba(0,0,0,0.1);display:none;max-height:240px;overflow-y:auto;z-index:2147483001;max-width:100vw;width:100%;min-width:340px;">
                ${createVariantDropdownItems()}
              </div>
            </div>
            <div class="sticky-cta-dropdown" id="sticky-purchase-dropdown-mobile" style="position:relative;flex:1;min-width:0;">
              <button type="button" class="sticky-cta-dropdown-button" style="display:flex;align-items:center;gap:8px;padding:17px 12px;border-radius:8px;font-size:12px;color:#ffffff;cursor:pointer;background:#454545;width:100%;border:1px solid #FFFFFF1A;justify-content:space-between;touch-action:manipulation;">
                <span id="sticky-purchase-label-mobile" style="white-space:nowrap;overflow:hidden;text-overflow:ellipsis;pointer-events:none;">${state.currentPurchaseLabel}</span>
                <svg style="width:12px;height:12px;flex-shrink:0;pointer-events:none;" viewBox="0 0 12 8" fill="none"><path d="M1 1.5L6 6.5L11 1.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
              </button>
              <div class="sticky-cta-dropdown-menu" style="position:absolute;bottom:100%;left:-170px;right:0;background:#454545;border:1px solid #d1d1d1;border-radius:8px;margin-bottom:8px;box-shadow:0 4px 12px rgba(0,0,0,0.1);display:none;max-height:240px;overflow-y:auto;z-index:2147483001;max-width:100vw;width:340px;">
                ${createPurchaseDropdownItems()}
              </div>
            </div>
          </div>
          ${atcButtonHTML(true)}
        </div>
      </div>
    `;
    document.body.appendChild(stickyBar);

    const style = document.createElement('style');
    style.textContent = `
      @media (max-width: 768px) {
        #${CONFIG.stickyBarId} .sticky-cta-desktop { display: none !important; }
        #${CONFIG.stickyBarId} .sticky-cta-mobile { display: block !important; }
      }
    `;
    document.head.appendChild(style);
  }

  function createVariantDropdownItems() {
    if (!state.variants.length) return '<div class="sticky-cta-dropdown-item">' + T.noVariants + '</div>';
    return state.variants.map(function (variant) {
      const isSelected = String(variant.id) === String(state.currentVariantId);
      const soldOut = variant.available === false;
      return `
        <div class="sticky-cta-dropdown-item" data-variant-id="${escapeAttr(variant.id)}" style="color:white;display:flex;align-items:center;gap:12px;padding:12px 16px;cursor:${soldOut ? 'not-allowed' : 'pointer'};font-size:13px;border-bottom:1px solid #323232;touch-action:manipulation;${isSelected ? 'background:#454545;font-weight:500;' : ''}${soldOut ? 'opacity:0.45;' : ''}">
          <span style="display:inline-block;width:18px;height:18px;border-radius:50%;border:2px solid ${isSelected ? '#f1b434' : '#888'};background:${isSelected ? '#f1b434' : 'transparent'};box-shadow:${isSelected ? 'inset 0 0 0 4px #2C2C2C' : 'none'};flex-shrink:0;pointer-events:none;"></span>
          ${variant.image ? `<img src="${escapeAttr(variant.image)}" style="width:30px;height:30px;object-fit:contain;border-radius:4px;flex-shrink:0;pointer-events:none;">` : ''}
          <span style="flex:1;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;pointer-events:none;">${variant.label}${soldOut ? ' — Sold Out' : ''}</span>
          <span style="flex-shrink:0;margin-left:auto;pointer-events:none;">${variant.price}</span>
        </div>
      `;
    }).join('');
  }

  function createPurchaseDropdownItems() {
    const v = currentVariant();
    const hasSubscription = !!(v && v.allocations && v.allocations.length);

    const items = [{ value: 'onetime', label: T.oneTime, price: state.onetimePrice }];
    if (hasSubscription) {
      items.push({ value: 'subscription', label: state.subscribeLabel, price: state.subscriptionPrice });
    }

    return items.map(function (item) {
      const isSelected = (item.value === 'onetime' && !state.isPurchaseTypeSubscription) ||
                         (item.value === 'subscription' && state.isPurchaseTypeSubscription);
      return `
        <div class="sticky-cta-dropdown-item" data-purchase-value="${item.value}" style="color:white;display:flex;align-items:center;gap:12px;padding:12px 16px;cursor:pointer;font-size:13px;border-bottom:1px solid #323232;touch-action:manipulation;${isSelected ? 'background:#454545;font-weight:500;' : ''}">
          <span style="display:inline-block;width:18px;height:18px;border-radius:50%;border:2px solid ${isSelected ? '#f1b434' : '#888'};background:${isSelected ? '#f1b434' : 'transparent'};box-shadow:${isSelected ? 'inset 0 0 0 4px #2C2C2C' : 'none'};flex-shrink:0;pointer-events:none;"></span>
          <span style="flex:1;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;pointer-events:none;">${item.label}</span>
          <span style="flex-shrink:0;margin-left:auto;pointer-events:none;">${item.price}</span>
        </div>
      `;
    }).join('');
  }

  // ============================================================
  // bindTap — unified touch + click handler (dropdowns)
  // ============================================================
  function bindTap(element, handler) {
    if (!element) return;
    let touchStartY = 0, touchStartX = 0, touchHandled = false;

    element.addEventListener('touchstart', function (e) {
      touchStartY = e.touches[0].clientY;
      touchStartX = e.touches[0].clientX;
    }, { passive: true });

    element.addEventListener('touchend', function (e) {
      const dy = Math.abs(e.changedTouches[0].clientY - touchStartY);
      const dx = Math.abs(e.changedTouches[0].clientX - touchStartX);
      if (dy > 8 || dx > 8) return;
      e.preventDefault();
      scheduleBlockNextClick();
      touchHandled = true;
      handler.call(this, e);
      setTimeout(function () { touchHandled = false; }, 500);
    }, { passive: false });

    element.addEventListener('click', function (e) {
      if (touchHandled) { e.preventDefault(); return; }
      handler.call(this, e);
    });
  }

  // ============================================================
  // bindAtcTap — ATC buttons only, no ghost-click block
  // ============================================================
  function bindAtcTap(element, handler) {
    if (!element) return;
    let touchStartY = 0, touchStartX = 0, touchHandled = false;

    element.addEventListener('touchstart', function (e) {
      touchStartY = e.touches[0].clientY;
      touchStartX = e.touches[0].clientX;
    }, { passive: true });

    element.addEventListener('touchend', function (e) {
      const dy = Math.abs(e.changedTouches[0].clientY - touchStartY);
      const dx = Math.abs(e.changedTouches[0].clientX - touchStartX);
      if (dy > 8 || dx > 8) return;
      e.preventDefault();
      touchHandled = true;
      handler.call(this, e);
      setTimeout(function () { touchHandled = false; }, 500);
    }, { passive: false });

    element.addEventListener('click', function (e) {
      if (touchHandled) { e.preventDefault(); return; }
      handler.call(this, e);
    });
  }

  // ============================================================
  // EVENT LISTENERS
  // ============================================================
  function attachEventListeners() {
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleScroll, { passive: true });

    setupDropdown('sticky-variant-dropdown', 'variant');
    setupDropdown('sticky-purchase-dropdown', 'purchase');
    setupDropdown('sticky-variant-dropdown-mobile', 'variant');
    setupDropdown('sticky-purchase-dropdown-mobile', 'purchase');

    function setupDropdown(id, type) {
      const dropdown = document.getElementById(id);
      if (!dropdown) return;
      const button = dropdown.querySelector('.sticky-cta-dropdown-button');
      const menu = dropdown.querySelector('.sticky-cta-dropdown-menu');
      bindTap(button, function (e) {
        e.preventDefault();
        const wasOpen = menu.style.display === 'block';
        closeAllDropdowns();
        if (!wasOpen) {
          dropdown.classList.add('open');
          menu.style.display = 'block';
          dropdownOpenTime = Date.now();
          anyDropdownOpen = true;
          setDropdownOpenState(true);
        }
      });
      bindMenuItems(dropdown, menu, type);
    }

    function bindMenuItems(dropdown, menu, type) {
      dropdown.querySelectorAll('.sticky-cta-dropdown-item').forEach(function (item) {
        bindTap(item, function () {
          if (Date.now() - dropdownOpenTime < 350) return;
          if (type === 'variant') {
            const id = item.getAttribute('data-variant-id');
            if (id) handleVariantSelection(id);
          } else {
            const value = item.getAttribute('data-purchase-value');
            if (value) handlePurchaseTypeSelection(value);
          }
          menu.style.display = 'none';
          dropdown.classList.remove('open');
          anyDropdownOpen = false;
          setDropdownOpenState(false);
        });
      });
    }
    attachEventListeners._bindMenuItems = bindMenuItems;

    function closeAllDropdowns() {
      document.querySelectorAll('.sticky-cta-dropdown').forEach(function (dd) {
        dd.classList.remove('open');
        const menu = dd.querySelector('.sticky-cta-dropdown-menu');
        if (menu) menu.style.display = 'none';
      });
      anyDropdownOpen = false;
      setDropdownOpenState(false);
    }

    let docTouchStartY = 0, docTouchStartX = 0;
    document.addEventListener('touchstart', function (e) {
      docTouchStartY = e.touches[0].clientY;
      docTouchStartX = e.touches[0].clientX;
    }, { passive: true });

    document.addEventListener('touchend', function (e) {
      const dy = Math.abs(e.changedTouches[0].clientY - docTouchStartY);
      const dx = Math.abs(e.changedTouches[0].clientX - docTouchStartX);
      if (dy > 8 || dx > 8) return;
      const insideStickyBar = e.target.closest('#' + CONFIG.stickyBarId);
      if (!insideStickyBar && anyDropdownOpen) {
        closeAllDropdowns();
        scheduleBlockNextClick();
      }
    }, { passive: true });

    document.addEventListener('click', function (e) {
      if (!e.target.closest('#' + CONFIG.stickyBarId)) closeAllDropdowns();
    });

    bindAtcTap(document.getElementById('sticky-add-to-cart'), handleStickyAddToCart);
    bindAtcTap(document.getElementById('sticky-add-to-cart-mobile'), handleStickyAddToCart);

    document.addEventListener('change', function (e) {
      const target = e.target;
      const variantContainer = getVariantContainer();
      if (variantContainer && variantContainer.contains(target)) {
        if (target.matches('input[type="radio"]') || target.matches('select')) {
          syncFromOriginalVariant();
          setTimeout(syncSoldOutState, 300);
        }
      }
      if (target.matches('input[name="selling_plan"]')) {
        syncFromOriginalPurchaseType();
        schedulePriceUpdate();
      }
      if (target.matches('input[name="id"]')) {
        syncFromOriginalVariant();
      }
    });

    const variantContainer = getVariantContainer();
    if (variantContainer) {
      let observerTimer = null;
      const observer = new MutationObserver(function (mutations) {
        let shouldSync = false;
        for (const mutation of mutations) {
          if (mutation.type === 'childList' ||
             (mutation.type === 'attributes' && mutation.attributeName === 'data-selected-variant')) {
            shouldSync = true;
            break;
          }
        }
        if (shouldSync) {
          if (observerTimer) clearTimeout(observerTimer);
          observerTimer = setTimeout(function () {
            syncFromOriginalVariant();
            schedulePriceUpdate();
            observerTimer = null;
          }, 150);
        }
      });
      observer.observe(variantContainer, { childList: true, subtree: true, attributes: true });
    }

    const rechargeWidget = document.getElementById(CONFIG.rechargeWidgetId);
    if (rechargeWidget) {
      const priceObserver = new MutationObserver(function () { schedulePriceUpdate(); });
      priceObserver.observe(rechargeWidget, { childList: true, subtree: true, characterData: true });
    }
  }

  function schedulePriceUpdate() {
    if (priceUpdateTimer) clearTimeout(priceUpdateTimer);
    priceUpdateTimer = setTimeout(function () {
      recomputePrices();
      if (!anyDropdownOpen) refreshStickyDropdowns();
      updateStickyPrice();
      updateStickyPurchaseDisplay();
      updateStickyVariantDisplay();
      syncSoldOutState();
      priceUpdateTimer = null;
    }, 200);
  }

  function refreshStickyDropdowns() {
    const bindMenuItems = attachEventListeners._bindMenuItems;
    ['sticky-variant-dropdown', 'sticky-variant-dropdown-mobile'].forEach(function (id) {
      const dropdown = document.getElementById(id);
      if (!dropdown) return;
      const menu = dropdown.querySelector('.sticky-cta-dropdown-menu');
      if (!menu) return;
      menu.innerHTML = createVariantDropdownItems();
      if (bindMenuItems) bindMenuItems(dropdown, menu, 'variant');
    });
    ['sticky-purchase-dropdown', 'sticky-purchase-dropdown-mobile'].forEach(function (id) {
      const dropdown = document.getElementById(id);
      if (!dropdown) return;
      const menu = dropdown.querySelector('.sticky-cta-dropdown-menu');
      if (!menu) return;
      menu.innerHTML = createPurchaseDropdownItems();
      if (bindMenuItems) bindMenuItems(dropdown, menu, 'purchase');
    });
  }

  function startPeriodicSync() {
    if (syncInterval) clearInterval(syncInterval);
    syncInterval = setInterval(function () {
      const stickyBar = document.getElementById(CONFIG.stickyBarId);
      if (!stickyBar || stickyBar.style.display === 'none') return;
      if (anyDropdownOpen) return;
      syncFromOriginalVariant();
      syncFromOriginalPurchaseType();
      syncSoldOutState();
    }, 2000);
  }

  /**
   * The main product section — gallery + info + variant picker + Add to Cart.
   * Resolved from the cart form upwards, which is template-agnostic: it works
   * on product.json, the .honey-spoon / .throat-spray / .sachet variants and
   * the Shogun template, none of which share a fixed section id.
   * Verified live: #shopify-section-template--<id>__main.
   */
  let _productSection = null;
  let _productSectionResolved = false;

  function getProductSection() {
    if (_productSectionResolved) return _productSection;

    const form = getProductForm();
    let el = form && form.closest('.shopify-section');

    if (!el) {
      el = document.querySelector('.shopify-section[id*="__main"]') ||
           document.querySelector('[id*="main-product"]');
    }
    if (!el) {
      const vs = document.querySelector('variant-selects');
      el = vs && vs.closest('.shopify-section');
    }
    // Guard against resolving to something tiny/wrong (e.g. a bare form).
    if (el && el.getBoundingClientRect().height < 200) el = null;

    // Cache only on success, so a late-rendering template still resolves.
    if (el) {
      _productSection = el;
      _productSectionResolved = true;
    }
    return el || null;
  }

  /**
   * The TRIGGER element — the bar appears once this has fully scrolled past.
   *
   * Per Ankush 10-Aug (final): the bar must appear "as soon as ACCREDITATIONS
   * & QUALITY ASSURANCE section starts" — when that section's TOP edge enters
   * the viewport, not when anything finishes.
   *
   * Measured live on UMF 24+:
   *   .c-product__umf          ends 3937   <-- trigger
   *   .product__info-container ends 3937
   *   main product section     ends 3961
   *   accreditations           ends 5094   (1157px too late)
   *
   * Resolution order:
   *   1. .c-product__umf — the UMF scale block. Rendered by the `product_list`
   *      block in main-product.liquid and only when `block.settings.product_list`
   *      is set, so it is NOT on every product.
   *   2. .product__info-container / [id^="ProductInfo-"] — the product info
   *      column, present on every template that uses main-product.liquid.
   *      Ends at the same point, so non-UMF products behave identically.
   *   3. the main product section.
   *   4. the pixel offset (handled by the caller).
   */
  let _triggerSection = null;
  let _triggerResolved = false;

  function usable(el) {
    if (!el) return false;
    const cs = getComputedStyle(el);
    if (cs.display === 'none' || cs.visibility === 'hidden') return false;
    return el.getBoundingClientRect().height > 10;
  }

  function getTriggerSection() {
    if (_triggerResolved) return _triggerSection;

    const main = getProductSection();
    if (!main) return null;

    const all = Array.prototype.slice.call(document.querySelectorAll('.shopify-section'));
    const idx = all.indexOf(main);
    let el = null;

    if (idx > -1) {
      // 1. the ACCREDITATIONS & QUALITY ASSURANCE section, which sits directly
      //    under the product template section. Its id carries a random
      //    per-template hash (…__accreditation_ypyWCK) so match on the word.
      for (let i = idx + 1; i < all.length; i++) {
        if (/accreditation/i.test(all[i].id) && usable(all[i])) { el = all[i]; break; }
      }
      // 2. next substantial section after main — covers the market-context
      //    templates (australia / canada / new-zealand) that have no
      //    accreditation section.
      if (!el) {
        for (let j = idx + 1; j < all.length; j++) {
          if (all[j].getBoundingClientRect().height > 100) { el = all[j]; break; }
        }
      }
    }
    if (!usable(el)) el = main;

    _triggerSection = el;
    _triggerResolved = true;
    return el;
  }

  /** True once the trigger section has finished — same rule on both breakpoints. */
  function shouldShowBar() {
    const section = getTriggerSection();
    if (!section) {
      // Fallback only: nothing resolvable, revert to the pixel offset.
      return (window.scrollY || window.pageYOffset) > CONFIG.showOffset;
    }
    // STARTS, not ends: fire the moment the section's top edge comes into view.
    return section.getBoundingClientRect().top <= (window.innerHeight - CONFIG.sectionEnterGap);
  }

  function handleScroll() {
    const stickyBar = document.getElementById(CONFIG.stickyBarId);
    if (!stickyBar) return;
    const shouldShow = shouldShowBar();
    if (shouldShow && !state.isVisible) {
      stickyBar.style.display = 'block';
      requestAnimationFrame(function () { stickyBar.style.transform = 'translateY(0)'; });
      state.isVisible = true;
      startGorgiasSync();
    } else if (!shouldShow && state.isVisible) {
      stickyBar.style.transform = 'translateY(100%)';
      setTimeout(function () { if (!state.isVisible) stickyBar.style.display = 'none'; }, 300);
      state.isVisible = false;
      stopGorgiasSync();
    }
  }

  // ============================================================
  // SELECTION HANDLERS
  // ============================================================
  /**
   * Picking a variant makes the theme scroll its own variant picker into view —
   * measured at 1,543px of upward jump from the sticky bar (10-Aug). Per Amanda:
   * you should only be taken back up when you choose SUBSCRIBE & SAVE; on
   * one-time you stay put and add to cart from where you are. So pin the scroll
   * position for a short window after a one-time variant change.
   */
  function holdScroll(y, ms) {
    const start = Date.now();
    (function pin() {
      if (Math.abs((window.scrollY || window.pageYOffset) - y) > 2) window.scrollTo(0, y);
      if (Date.now() - start < ms) requestAnimationFrame(pin);
    })();
  }

  function handleVariantSelection(variantId) {
    // subscription selected => the jump up to the Recharge widget is intended
    const keepPosition = !state.isPurchaseTypeSubscription;
    const yBefore = window.scrollY || window.pageYOffset;
    let target = null;
    for (let i = 0; i < productData.variants.length; i++) {
      if (productData.variants[i].id === String(variantId)) { target = productData.variants[i]; break; }
    }
    if (!target) return;

    const container = getVariantContainer();
    if (container) {
      // Drive every option group so multi-option products resolve correctly.
      target.options.forEach(function (optValue) {
        const radio = container.querySelector('input[type="radio"][value="' + (window.CSS && CSS.escape ? CSS.escape(optValue) : optValue) + '"]');
        if (radio) {
          if (!radio.checked) {
            radio.checked = true;
            radio.dispatchEvent(new Event('change', { bubbles: true }));
          }
          return;
        }
        const selects = container.querySelectorAll('select');
        selects.forEach(function (sel) {
          for (let i = 0; i < sel.options.length; i++) {
            if (sel.options[i].value === optValue) {
              sel.value = optValue;
              sel.dispatchEvent(new Event('change', { bubbles: true }));
              break;
            }
          }
        });
      });
    }

    if (keepPosition) holdScroll(yBefore, 1200);

    state.currentVariantId = target.id;
    recomputePrices();
    updateStickyVariantDisplay();
    updateStickyPrice();
    refreshStickyDropdowns();
    setTimeout(syncSoldOutState, 400);
  }

  function handlePurchaseTypeSelection(value) {
    const isSubscription = value === 'subscription';
    state.isPurchaseTypeSubscription = isSubscription;
    triggerRechargeV2Selection(value);
    if (isSubscription) {
      const rechargeBlock = document.querySelector('recharge-subscription-widget');
      if (rechargeBlock) rechargeBlock.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
    recomputePrices();
    updateStickyPurchaseDisplay();
    updateStickyVariantDisplay();
    updateStickyPrice();
    refreshStickyDropdowns();
  }

  function syncFromOriginalVariant() {
    const resolved = resolveCurrentVariant();
    if (!resolved) return;
    if (String(resolved.id) !== String(state.currentVariantId)) {
      state.currentVariantId = resolved.id;
      recomputePrices();
      updateStickyVariantDisplay();
      updateStickyPrice();
      if (!anyDropdownOpen) refreshStickyDropdowns();
    }
  }

  function syncFromOriginalPurchaseType() {
    const isSubscription = isSubscriptionActiveFromDOM();
    if (isSubscription !== state.isPurchaseTypeSubscription) {
      state.isPurchaseTypeSubscription = isSubscription;
      recomputePrices();
      updateStickyPurchaseDisplay();
      updateStickyVariantDisplay();
      updateStickyPrice();
      refreshStickyDropdowns();
    }
  }

  // ============================================================
  // DISPLAY UPDATES
  // ============================================================
  function updateStickyVariantDisplay() {
    const buttonText = state.currentVariantLabel
      ? `${state.currentVariantLabel} – ${state.currentPrice}`
      : T.selectVariant;

    [['#sticky-variant-dropdown', '#sticky-variant-label', 24],
     ['#sticky-variant-dropdown-mobile', '#sticky-variant-label-mobile', 20]
    ].forEach(function (cfg) {
      const button = document.querySelector(cfg[0] + ' .sticky-cta-dropdown-button');
      if (!button) return;
      const labelSpan = button.querySelector(cfg[1]);
      const img = button.querySelector('.sticky-variant-img');
      if (labelSpan) labelSpan.textContent = buttonText;
      if (img) {
        img.src = state.currentVariantImage || CONFIG.productImage;
      } else if (state.currentVariantImage) {
        const newImg = document.createElement('img');
        newImg.src = state.currentVariantImage;
        newImg.className = 'sticky-variant-img';
        newImg.style.cssText = `width:${cfg[2]}px;height:${cfg[2]}px;object-fit:contain;border-radius:4px;flex-shrink:0;pointer-events:none;`;
        const flexSpan = button.querySelector('span');
        if (flexSpan) flexSpan.prepend(newImg);
      }
    });
  }

  function updateStickyPurchaseDisplay() {
    ['sticky-purchase-label', 'sticky-purchase-label-mobile'].forEach(function (id) {
      const span = document.getElementById(id);
      if (span) span.textContent = state.currentPurchaseLabel;
    });
  }

  function updateStickyPrice() {
    if (state.isSoldOut) return;
    ['sticky-price', 'sticky-price-mobile'].forEach(function (id) {
      const span = document.getElementById(id);
      if (span) span.textContent = state.currentPrice;
    });
  }

  function handleStickyAddToCart(e) {
    if (state.isSoldOut) return;
    if (e) e.preventDefault();

    const btn = e && e.currentTarget
      ? e.currentTarget
      : document.getElementById('sticky-add-to-cart-mobile') || document.getElementById('sticky-add-to-cart');

    const submitButton = document.querySelector(
      'button[type="submit"][name="add"], [id^="ProductSubmitButton-"]'
    );

    if (submitButton && !submitButton.disabled) {
      submitButton.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true, view: window }));
    } else {
      const originalForm = getProductForm();
      if (originalForm) {
        const formSubmit = originalForm.querySelector('button[type="submit"]');
        if (formSubmit) {
          formSubmit.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true, view: window }));
        } else {
          originalForm.submit();
        }
      }
    }

    if (btn) {
      const originalText = btn.innerHTML;
      btn.innerHTML = 'Adding...';
      btn.disabled = true;
      setTimeout(function () { btn.innerHTML = originalText; btn.disabled = false; }, 1500);
    }
  }

  // ============================================================
  // FLOATING WIDGETS — Gorgias live chat + UserWay accessibility
  //
  // Both are position:fixed at the bottom of the viewport and both sit at a
  // higher z-index than the bar, so without this they paint straight over it:
  //   Gorgias  — bottom ~20px, right ~35px  (z 2147483636)
  //   UserWay  — bottom  13px, left   13px  (44x44, bottom-LEFT)
  // Offsets are measured from the bar's real rendered height rather than the
  // hardcoded 100/140px in the original build, so they stay correct on both
  // breakpoints and if the bar's content ever reflows to two lines.
  // ============================================================
  let widgetInterval = null;
  const WIDGET_GAP = 12;

  function stickyBarHeight() {
    const bar = document.getElementById(CONFIG.stickyBarId);
    if (!bar || bar.style.display === 'none') return 0;
    return Math.ceil(bar.getBoundingClientRect().height);
  }

  function gorgiasFrame() {
    return document.querySelector('iframe[title="Gorgias live chat messenger"]');
  }

  function userwayWidget() {
    return document.querySelector('.userway_buttons_wrapper');
  }

  // DELIBERATELY NOT LIFTED: the Klaviyo signup teaser ([class*="kl-teaser"])
  // and the Rebuy bubble (#rebuy-bubble-global-alert-modal). Both were tested —
  // lifting the Klaviyo teaser pushes it straight into the chat launcher
  // (teaser x 101-385 vs chat x 297-465 at 500px wide), and the teaser is not
  // reliably viewport-fixed, so forcing a bottom offset misplaces it. Both sit
  // BELOW the bar's z-index, so they simply render behind it. See the handover
  // note: this is a known, accepted trade-off for the test.
  function updateFloatingWidgets() {
    const lift = stickyBarHeight() + WIDGET_GAP;

    const chat = gorgiasFrame();
    if (chat) chat.style.setProperty('bottom', lift + 'px', 'important');

    // Keep the accessibility widget VISIBLE — just move it clear of the bar.
    const uw = userwayWidget();
    if (uw) {
      uw.style.setProperty('bottom', lift + 'px', 'important');
      uw.style.setProperty('top', 'auto', 'important');
    }
  }

  function startGorgiasSync() {
    updateFloatingWidgets();
    if (widgetInterval) return;
    // Both widgets re-assert their own inline styles after they (re)render,
    // so this keeps the offset applied.
    widgetInterval = setInterval(updateFloatingWidgets, 300);
  }

  function stopGorgiasSync() {
    if (widgetInterval) { clearInterval(widgetInterval); widgetInterval = null; }

    const chat = gorgiasFrame();
    if (chat) {
      if (window.innerWidth <= CONFIG.mobileBreakpoint) {
        chat.style.setProperty('bottom', '20px', 'important');
      } else {
        chat.style.removeProperty('bottom');
        chat.style.removeProperty('right');
      }
    }

    const uw = userwayWidget();
    if (uw) {
      uw.style.setProperty('bottom', '13px', 'important');
      uw.style.setProperty('top', 'auto', 'important');
    }
  }

  init();
})();
